import { createBrowserRouter, Navigate, RouterProvider } from 'react-router-dom'
import { ThemeProvider } from '@/lib/theme'
import { SectionProvider } from '@/lib/section'
import { AppShell } from '@/components/layout/AppShell'
import { AdminLayout } from '@/components/admin/AdminLayout'
import { RouteError } from '@/components/RouteError'
import { RequireAuth } from '@/features/identity/RequireAuth'

// Leaf routes are code-split via React Router's native `lazy` option (not
// React.lazy): the data router downloads each page's chunk during navigation,
// so the heavy areas — the Mux player (watch + assessment), the dnd-kit admin
// editors (track/segment editing), and the Knowledgebase — stay off the initial
// bundle and only load when their route is visited. The entry keeps just the
// core (react / router / query / axios) plus the always-mounted shells
// (RequireAuth, AppShell, AdminLayout). `lazy` is used rather than React.lazy to
// avoid the data router's "suspended on synchronous input" remount. Pages are
// named exports, so each loader maps the export onto the router's `Component`.
const router = createBrowserRouter([
  // Login is the one page outside the auth gate (no header/nav).
  {
    path: '/login',
    errorElement: <RouteError />,
    lazy: () => import('@/features/identity/LoginPage').then((m) => ({ Component: m.LoginPage })),
  },
  // Everything else sits behind RequireAuth: it renders a neutral splash until
  // /api/v1/me resolves, so an unauthenticated visitor goes straight to /login
  // with no flash of app chrome.
  {
    element: <RequireAuth />,
    errorElement: <RouteError />,
    children: [
      {
        element: <AppShell />,
        errorElement: <RouteError />,
        children: [
          { path: '/', lazy: () => import('@/features/learning/LibraryPage').then((m) => ({ Component: m.LibraryPage })) },
          { path: '/journey', lazy: () => import('@/features/learning/DashboardPage').then((m) => ({ Component: m.DashboardPage })) },
          { path: '/leaderboard', lazy: () => import('@/features/recognition/LeaderboardPage').then((m) => ({ Component: m.LeaderboardPage })) },
          { path: '/orgs/:orgId', lazy: () => import('@/features/analytics/ClientDetailPage').then((m) => ({ Component: () => <m.ClientDetailPage tab="analytics" /> })) },
          { path: '/orgs/:orgId/assignments', lazy: () => import('@/features/analytics/ClientDetailPage').then((m) => ({ Component: () => <m.ClientDetailPage tab="assignments" /> })) },
          { path: '/orgs/:orgId/leaderboard', lazy: () => import('@/features/analytics/ClientDetailPage').then((m) => ({ Component: () => <m.ClientDetailPage tab="leaderboard" /> })) },
          { path: '/tracks/:slug', lazy: () => import('@/features/learning/TrackDetailPage').then((m) => ({ Component: m.TrackDetailPage })) },
          { path: '/tracks/:slug/assessment', lazy: () => import('@/features/assessment/AssessmentPage').then((m) => ({ Component: m.AssessmentPage })) },
          { path: '/watch/:slug', lazy: () => import('@/features/learning/WatchPage').then((m) => ({ Component: m.WatchPage })) },
          { path: '/knowledge-base', lazy: () => import('@/features/knowledgebase/KnowledgebasePage').then((m) => ({ Component: m.KnowledgebasePage })) },
          { path: '/knowledge-base/:slug', lazy: () => import('@/features/knowledgebase/ArticlePage').then((m) => ({ Component: m.ArticlePage })) },
          // Global "What's New" — reachable by clients and staff; the server
          // gates entries by visibility and returns empty when the flag is off.
          { path: '/whats-new', lazy: () => import('@/features/whatsnew/WhatsNewPage').then((m) => ({ Component: m.WhatsNewPage })) },
          { path: '/search', lazy: () => import('@/features/learning/SearchPage').then((m) => ({ Component: m.SearchPage })) },
          // Admin CMS lives inside the main shell (left sidebar) per the pass-2
          // design; AdminLayout adds the horizontal tab sub-nav + access gate.
          {
            path: 'admin',
            element: <AdminLayout />,
            errorElement: <RouteError />,
            children: [
              { index: true, element: <Navigate to="/admin/tracks" replace /> },
              { path: 'tracks', lazy: () => import('@/features/learning/AdminTracksPage').then((m) => ({ Component: m.AdminTracksPage })) },
              { path: 'tracks/new', lazy: () => import('@/features/learning/AdminTrackEditPage').then((m) => ({ Component: m.AdminTrackEditPage })) },
              { path: 'tracks/:slug', lazy: () => import('@/features/learning/AdminTrackEditPage').then((m) => ({ Component: m.AdminTrackEditPage })) },
              { path: 'segments', lazy: () => import('@/features/learning/AdminSegmentsPage').then((m) => ({ Component: m.AdminSegmentsPage })) },
              { path: 'segments/new', lazy: () => import('@/features/learning/AdminSegmentEditPage').then((m) => ({ Component: m.AdminSegmentEditPage })) },
              { path: 'segments/:slug', lazy: () => import('@/features/learning/AdminSegmentEditPage').then((m) => ({ Component: m.AdminSegmentEditPage })) },
              { path: 'audiences', lazy: () => import('@/features/assignments/AudiencesPage').then((m) => ({ Component: m.AudiencesPage })) },
              { path: 'badges', lazy: () => import('@/features/recognition/BadgesPage').then((m) => ({ Component: m.BadgesPage })) },
              { path: 'hubspot', lazy: () => import('@/features/crm/HubspotPage').then((m) => ({ Component: m.HubspotPage })) },
              { path: 'knowledgebase', lazy: () => import('@/features/knowledgebase/KbPage').then((m) => ({ Component: m.KbPage })) },
              { path: 'whats-new', lazy: () => import('@/features/whatsnew/AdminWhatsNewPage').then((m) => ({ Component: m.AdminWhatsNewPage })) },
              // Owner-only (super_admin): gated inside each page on me.canManageTeam.
              { path: 'team', lazy: () => import('@/features/team/AdminTeamPage').then((m) => ({ Component: m.AdminTeamPage })) },
              { path: 'audit-log', lazy: () => import('@/features/team/AdminAuditLogPage').then((m) => ({ Component: m.AdminAuditLogPage })) },
              { path: '*', lazy: () => import('@/app/NotFoundPage').then((m) => ({ Component: m.NotFoundPage })) },
            ],
          },
          { path: '*', lazy: () => import('@/app/NotFoundPage').then((m) => ({ Component: m.NotFoundPage })) },
        ],
      },
    ],
  },
])

export function App() {
  return (
    <ThemeProvider>
      <SectionProvider>
        <RouterProvider router={router} />
      </SectionProvider>
    </ThemeProvider>
  )
}
